import { estadoDesdeIVT } from './umbralesVITA';
export function buildPredictiveEngine(snapshots = [], actorId) {
  // Filtrar snapshots del actor ordenados por fecha
  const actorSnapshots = snapshots
    .filter((s) => s.actorId === actorId)
    .sort((a, b) => new Date(a.fecha) - new Date(b.fecha));

  if (actorSnapshots.length === 0) {
    return buildInsuficiente('Sin historial de snapshots para este actor.');
  }

  if (actorSnapshots.length === 1) {
    return buildInsuficiente('Se requieren al menos 2 snapshots para calcular trayectoria.');
  }

  const primero = actorSnapshots[0];
  const ultimo = actorSnapshots[actorSnapshots.length - 1];
  const penultimo = actorSnapshots[actorSnapshots.length - 2];

  // Calcular velocidad de cambio IVT
  const diasTranscurridos = calcularDias(primero.fecha, ultimo.fecha);
  const cambioIVT = (ultimo.IVT || 0) - (primero.IVT || 0);
  const velocidadIVT = diasTranscurridos > 0
    ? cambioIVT / diasTranscurridos
    : 0;

  // Calcular aceleración (cambio reciente vs cambio total)
  const cambioReciente = (ultimo.IVT || 0) - (penultimo.IVT || 0);
  const aceleracion = cambioReciente > cambioIVT / actorSnapshots.length
    ? 'acelerando'
    : cambioReciente < 0
      ? 'desacelerando'
      : 'estable';

  // Trayectoria de estados
  const trayectoriaEstados = actorSnapshots.map((s) => ({
    fecha: formatFecha(s.fecha),
    estado: s.estadoEngine || s.estadoTerritorial || 'Sin estado',
    IVT: s.IVT || 0
  }));

  // Proyección a 7 y 30 días
  const IVTProyectado7 = Math.min(
    Math.max(Math.round((ultimo.IVT || 0) + velocidadIVT * 7), 0),
    100
  );
  const IVTProyectado30 = Math.min(
    Math.max(Math.round((ultimo.IVT || 0) + velocidadIVT * 30), 0),
    100
  );

  // Estado proyectado
  const estadoProyectado7 = inferirEstadoDesdeIVT(IVTProyectado7);
  const estadoProyectado30 = inferirEstadoDesdeIVT(IVTProyectado30);

  // Umbral de activación
  const umbralActivacion = calcularUmbralActivacion(ultimo.IVT || 0, velocidadIVT);

  // Riesgo de escalamiento
  const riesgoEscalamiento = calcularRiesgoEscalamiento(
    ultimo.IVT || 0,
    velocidadIVT,
    aceleracion,
    ultimo.estadoEngine
  );

  // Narrativa predictiva
  const narrativaPredictiva = buildNarrativaPredictiva({
    actorNombre: ultimo.actorNombre,
    estadoActual: ultimo.estadoEngine || ultimo.estadoTerritorial,
    estadoProyectado7,
    estadoProyectado30,
    velocidadIVT,
    aceleracion,
    riesgoEscalamiento,
    umbralActivacion
  });

  return {
    confianza: actorSnapshots.length >= 3 ? 'Media' : 'Baja',
    totalSnapshots: actorSnapshots.length,
    diasMonitoreados: diasTranscurridos,
    IVTActual: ultimo.IVT || 0,
    IVTInicial: primero.IVT || 0,
    cambioIVT,
    velocidadIVT: Math.round(velocidadIVT * 10) / 10,
    aceleracion,
    trayectoriaEstados,
    proyeccion: {
      dias7: { IVT: IVTProyectado7, estado: estadoProyectado7 },
      dias30: { IVT: IVTProyectado30, estado: estadoProyectado30 }
    },
    umbralActivacion,
    riesgoEscalamiento,
    narrativaPredictiva,
    insuficiente: false
  };
}

// ─────────────────────────────────────────────
// PREDICCIÓN PARA PROYECTO COMPLETO
// Agrega predicciones de todos los actores
// ─────────────────────────────────────────────

export function buildProjectPrediction(snapshots = [], processedActors = []) {
  if (snapshots.length === 0 || processedActors.length === 0) {
    return {
      insuficiente: true,
      mensaje: 'Sin datos históricos suficientes para predicción territorial.',
      actoresEnEscalamiento: [],
      tendenciaGeneral: 'Sin datos',
      accionPrioritaria: 'Registrar cambios en stakeholders para activar el motor predictivo.'
    };
  }

  const prediccionesActores = processedActors
    .map((actor) => ({
      actor,
      prediccion: buildPredictiveEngine(snapshots, actor.id)
    }))
    .filter((item) => !item.prediccion.insuficiente);

  if (prediccionesActores.length === 0) {
    return {
      insuficiente: true,
      mensaje: 'Se necesitan al menos 2 actualizaciones por actor para calcular trayectorias.',
      actoresEnEscalamiento: [],
      tendenciaGeneral: 'Acumulando datos',
      accionPrioritaria: 'Continuar registrando cambios en stakeholders.'
    };
  }

  // Actores con riesgo de escalamiento alto
  const actoresEnEscalamiento = prediccionesActores
    .filter((item) =>
      item.prediccion.riesgoEscalamiento === 'Alto' ||
      item.prediccion.riesgoEscalamiento === 'Crítico'
    )
    .map((item) => ({
      nombre: item.actor.nombre,
      IVTActual: item.prediccion.IVTActual,
      IVTProyectado30: item.prediccion.proyeccion.dias30.IVT,
      estadoProyectado: item.prediccion.proyeccion.dias30.estado,
      riesgo: item.prediccion.riesgoEscalamiento
    }));

  // Tendencia general del proyecto
  const cambiosPromedio = prediccionesActores.reduce(
    (sum, item) => sum + item.prediccion.cambioIVT, 0
  ) / prediccionesActores.length;

  const tendenciaGeneral = cambiosPromedio > 5
    ? 'Escalando'
    : cambiosPromedio < -5
      ? 'Estabilizando'
      : 'Latente';

  // IVT promedio proyectado a 30 días
  const IVTProyectadoPromedio = Math.round(
    prediccionesActores.reduce(
      (sum, item) => sum + item.prediccion.proyeccion.dias30.IVT, 0
    ) / prediccionesActores.length
  );

  return {
    insuficiente: false,
    totalActoresConHistorial: prediccionesActores.length,
    actoresEnEscalamiento,
    tendenciaGeneral,
    IVTProyectadoPromedio,
    accionPrioritaria: buildAccionProyecto(actoresEnEscalamiento, tendenciaGeneral)
  };
}

// ─────────────────────────────────────────────
// UTILIDADES
// ─────────────────────────────────────────────

function calcularDias(fechaInicio, fechaFin) {
  const inicio = new Date(fechaInicio);
  const fin = new Date(fechaFin);
  const diff = fin - inicio;
  return Math.max(Math.round(diff / (1000 * 60 * 60 * 24)), 1);
}

function formatFecha(fechaISO) {
  if (!fechaISO) return 'Sin fecha';
  const fecha = new Date(fechaISO);
  return fecha.toLocaleDateString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
}

// Estado desde IVT — ahora usa la tabla única (umbralesVITA.js)
function inferirEstadoDesdeIVT(ivt) {
  return estadoDesdeIVT(ivt);
}

function calcularUmbralActivacion(IVTActual, velocidadIVT) {
  const umbral = 50;
  if (IVTActual >= umbral) return 'Superado — actor en activación o superior';
  if (velocidadIVT <= 0) return 'Sin tendencia de activación detectada';
  const diasHastaUmbral = Math.round((umbral - IVTActual) / velocidadIVT);
  if (diasHastaUmbral <= 7) return `Crítico — umbral en aproximadamente ${diasHastaUmbral} días`;
  if (diasHastaUmbral <= 30) return `Alerta — umbral en aproximadamente ${diasHastaUmbral} días`;
  return `Estable — umbral en aproximadamente ${diasHastaUmbral} días`;
}

function calcularRiesgoEscalamiento(IVTActual, velocidadIVT, aceleracion, estadoActual) {
  if (
    IVTActual >= 75 ||
    estadoActual === 'Escalamiento Territorial' ||
    (IVTActual >= 60 && aceleracion === 'acelerando')
  ) return 'Crítico';

  if (
    IVTActual >= 55 ||
    (velocidadIVT > 2 && IVTActual >= 40) ||
    estadoActual === 'Convergencia Narrativa'
  ) return 'Alto';

  if (
    IVTActual >= 40 ||
    velocidadIVT > 1
  ) return 'Medio';

  return 'Bajo';
}

function buildNarrativaPredictiva({
  actorNombre,
  estadoActual,
  estadoProyectado7,
  estadoProyectado30,
  velocidadIVT,
  aceleracion,
  riesgoEscalamiento,
  umbralActivacion
}) {
  const nombre = actorNombre || 'El actor';

  if (riesgoEscalamiento === 'Crítico') {
    return `${nombre} muestra una trayectoria de escalamiento crítico. Si la tendencia actual continúa, en 7 días podría estar en estado "${estadoProyectado7}" y en 30 días en "${estadoProyectado30}". Se requiere intervención inmediata.`;
  }

  if (riesgoEscalamiento === 'Alto') {
    const tendencia = aceleracion === 'acelerando'
      ? 'acelerando su trayectoria territorial'
      : 'manteniendo presión territorial sostenida';
    return `${nombre} está ${tendencia}. La proyección a 30 días indica estado "${estadoProyectado30}". Se recomienda gestión preventiva en los próximos 7 días.`;
  }

  if (velocidadIVT < 0) {
    return `${nombre} muestra una trayectoria de estabilización. Su IVT está disminuyendo, lo que indica reducción de presión territorial. Mantener monitoreo regular.`;
  }

  if (velocidadIVT === 0) {
    return `${nombre} mantiene una trayectoria estable sin cambios significativos. ${umbralActivacion}.`;
  }

  return `${nombre} muestra cambios territoriales moderados. La proyección a 30 días indica estado "${estadoProyectado30}". Mantener monitoreo activo.`;
}

function buildAccionProyecto(actoresEnEscalamiento, tendencia) {
  if (actoresEnEscalamiento.length >= 2) {
    return `${actoresEnEscalamiento.length} actores proyectan escalamiento. Activar gestión territorial preventiva prioritaria.`;
  }
  if (actoresEnEscalamiento.length === 1) {
    return `${actoresEnEscalamiento[0].nombre} proyecta escalamiento en 30 días. Iniciar contacto preventivo inmediato.`;
  }
  if (tendencia === 'Escalando') {
    return 'Territorio con tendencia creciente. Reforzar monitoreo y preparar estrategia preventiva.';
  }
  if (tendencia === 'Estabilizando') {
    return 'Territorio estabilizándose. Mantener gestión actual y monitoreo regular.';
  }
  return 'Territorio estable. Continuar monitoreo regular y actualizar stakeholders periódicamente.';
}

function buildInsuficiente(mensaje) {
  return {
    insuficiente: true,
    confianza: 'Sin datos',
    mensaje,
    totalSnapshots: 0,
    diasMonitoreados: 0,
    IVTActual: 0,
    cambioIVT: 0,
    velocidadIVT: 0,
    aceleracion: 'sin datos',
    trayectoriaEstados: [],
    proyeccion: {
      dias7: { IVT: 0, estado: 'Sin datos' },
      dias30: { IVT: 0, estado: 'Sin datos' }
    },
    umbralActivacion: 'Sin datos suficientes',
    riesgoEscalamiento: 'Sin datos',
    narrativaPredictiva: mensaje
  };
}